/* jshint esversion: 6, -W097 */
/* globals require, module */
'use strict';

/**
 * AppNexusTag Directive
 * @module enervee_utils.ads
 *
 * Given an appropriate tag name, this directive runs the apntag scripts
 * to load ads from AppNexus.
 *
 * @param {string}  tagname The name of the AppNexus tag to manage
 * @param {bool}    noStyle Boolean that specifies whether to show
 *                  default styling or not. Defaults to false.
 * @param {bool}    isDesktop Boolean that specifies whether this is
 *                  a desktop ad for styling purposes. Defaults to false.
 * @param {bool}    isMobile Boolean that specifies whether this is
 *                  a mobile ad for styling purposes. Defaults to false.
 * @param {string}  showTagEvent The name of the event the directive should
 *                  listen to before running the showTag function. Useful if
 *                  you want to delay the display of the ad. Call $broadcast
 *                  on a parent $scope to trigger this event.
 * @param {bool}    adLoaded a boolean that is updated to `true` when the ad loads.
 *                  Value is updated internally, so applying a value to it doesn't
 *                  do anything.
 *
 * Examples:
 * <app-nexus-tag data-tagname="'TAG-NAME-1'"
 *     data-no-style="true"
 *     data-is-desktop="true">
 * </app-nexus-tag>
 *
 * <div class="wrapper"
 *     ng-show="adLoaded">
 *     <app-nexus-tag data-tagname="'TAG-NAME-2'"
 *         data-show-tag-event="'displayTagName2'"
 *         data-ad-loaded="adLoaded">
 *     </app-nexus-tag>
 * </div>
 *
 */

function appNexusDirective() {
    return {
        scope: {
            tagname: '<',
            noStyle: '<?',
            isDesktop: '<?',
            isMobile: '<?',
            showTagEvent: '<?',
            adLoaded: '=?', // really a one-way (up-only)
        },
        template: require('./appNexusTagView.html'),
        bindToController: true,
        controllerAs: 'AppNexusTagCtrl',
        controller: [
            '$window',
            '$timeout',
            'AppNexusService',
            function(
                $window,
                $timeout,
                AppNexusService
            ) {
                this.$onInit = function () {
                    var vm = this;
                    vm.appNexusEnabled = AppNexusService.isEnabled;
                    vm.adLoaded = false;
                    vm._adLoaded = false;
                    vm.runShowTag = runShowTag;

                    // check if tag on page for correct device and don't display if not
                    var suffix = AppNexusService.isMobile() ? "MOB-" : "DSK-";
                    vm.validToShow = vm.tagname && vm.tagname.startsWith(suffix);

                    $window.apntag.anq.push(function () {
                        // When the ad is loaded, set var to display ad
                        $window.apntag.onEvent('adLoaded', vm.tagname, function displayAd() {
                            /**
                             * First $timeout is to sync up the changes from the AST API with
                             * a digest cycle. Second $timeout is to make sure that any logic
                             * affected by the public `adLoaded` property has a chance to run
                             * before _actually_ trying to load the ad.
                             */
                            $timeout(function () {
                                vm.adLoaded = true;
                                $timeout(function () {
                                    vm._adLoaded = true;
                                }, 100);
                            });
                        });
                    });

                    // Call to show/refresh tag
                    function runShowTag() {
                        if (vm.appNexusEnabled && vm.validToShow) {
                            $window.apntag.anq.push(function () {
                                AppNexusService.createTag(vm.tagname);
                                $window.apntag.showTag(vm.tagname);
                            });
                        }
                    }
                };
            }
        ],
        compile: appNexusTagCompile
    };

    function appNexusTagCompile(tElement, tAttrs) {
        // Set defaults
        tAttrs.noStyle = angular.isDefined(tAttrs.noStyle) ? tAttrs.noStyle : 'false';
        tAttrs.isDesktop = angular.isDefined(tAttrs.isDesktop) ? tAttrs.isDesktop : 'false';
        tAttrs.isMobile = angular.isDefined(tAttrs.isMobile) ? tAttrs.isMobile : 'false';
        return appNexusTagLink;
    }

    function appNexusTagLink(scope, iElement, iAttrs, controller) {
        // Holds the deregister function for created listeners
        var showTagListener;
        scope.$watch("AppNexusTagCtrl.showTagEvent", function(newVal) {
            if (showTagListener !== undefined) {
                // Deregister existing listener
                showTagListener();
            }
            if (newVal === undefined) {
                // There is no event to listen to, just run the function
                controller.runShowTag();
            } else {
                // Create a listener for the specified event
                showTagListener = scope.$on(newVal, controller.runShowTag);
            }
        }, true);
    }
}

module.exports = appNexusDirective;